/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo, ReactNode} from 'react';
import {makeStyles} from '@material-ui/core';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {IonSpinner} from '../components/loading/IonSpinner';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsColor, dlsZIndex} from '../theme/theme';
import {DLSTheme} from '../types/types';

// defines the IonGridContainer props definitions...
export interface IonGridContainerProps {
  children?: ReactNode;
};

// defines the IonGridContainer style definitions...
const useStyles = makeStyles((theme: DLSTheme) => ({
  visibleContainer : toggleThemeStyle({
    visibility : 'visible',
    height     : '100%'
  }, {
    width         : '100%',
    height        : '100%',
    display       : 'flex',
    visibility    : 'visible',
    flexDirection : 'column'
  }),
  hiddenContainer : {
    visibility : 'hidden'
  },
  loader : {
    height          : '90vh',
    width           : '100%',
    position        : 'relative',
    zIndex          : dlsZIndex('assetHistoryBackdrop'),
    backgroundColor : dlsColor('grey6'),
    display         : 'flex',
    justifyContent  : 'center',
    alignItems      : 'center'
  }
}) as any);

/**
 * Defines the DLS IonGridContainer component. This component is responsible for positioning
 * the all the grid container along with the page content with animation. It also can avoid the
 * showing of the grid before rendering other components in the page.
 *
 * ```typescript
 *
 *  import {IonGridContainer} from '@ion/dls/src/grid/';
 *
 *  export const AdminContainer = ({...props}) => {
 *   return (
 *     <IonGridContainer>
 *       {content}
 *     </IonGridContainer>
 *    );
 *  }
 *
 * ```
 */
export const IonGridContainer = ({
  children
}: IonGridContainerProps) => {
  const isMounted = useHasMounted();
  const classes   = useStyles({isMounted});

  return (
    <>
      {!isMounted && (
        <div className={classes.loader}>
          <IonSpinner />
        </div>
      )}
      <div className={classes[isMounted ? 'visibleContainer' : 'hiddenContainer']}>
        {children}
      </div>
    </>
  );
};

export default memo(IonGridContainer);
